import { useState, useEffect } from "react";
import { Link } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo, ArrowIcon } from "./_shared";

const NAV_LINKS = [
  { label: "Problema", href: "#problema" },
  { label: "Solução", href: "#solucao" },
  { label: "Como Funciona", href: "#como-funciona" },
  { label: "FAQ", href: "#faq" },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setIsOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleAnchorClick = (href: string) => {
    setIsOpen(false);
    const el = document.querySelector(href);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <nav
      className="fixed top-0 w-full z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(8,12,20,0.88)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled
          ? "1px solid var(--cy-ink-10)"
          : "1px solid transparent",
      }}
    >
      <div className="max-w-[1360px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <Logo size={32} textSize={20} />
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center" style={{ gap: 32 }}>
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => handleAnchorClick(link.href)}
              style={{
                fontFamily: "var(--cy-fs-sans)",
                fontSize: 14,
                color: "var(--cy-ink-80)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                transition: "color .15s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = "var(--cy-ink-100)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "var(--cy-ink-80)")
              }
            >
              {link.label}
            </button>
          ))}
          <a
            href="https://github.com/haporfirio/cyphron"
            target="_blank"
            rel="noopener noreferrer"
            className="cy-btn cy-btn--primary"
            style={{ padding: "10px 18px", fontSize: 14 }}
          >
            View on GitHub
            <span className="cy-arrow">
              <ArrowIcon size={12} color="#0A1500" />
            </span>
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden p-2"
          aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
          style={{
            color: "var(--cy-ink-80)",
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="md:hidden overflow-hidden"
            style={{
              background: "rgba(8,12,20,0.95)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderBottom: "1px solid var(--cy-ink-10)",
            }}
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleAnchorClick(link.href)}
                  className="text-left px-3 py-2.5 rounded-lg transition-colors"
                  style={{
                    fontFamily: "var(--cy-fs-sans)",
                    fontSize: 14,
                    fontWeight: 500,
                    color: "var(--cy-ink-80)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {link.label}
                </button>
              ))}
              <a
                href="https://github.com/haporfirio/cyphron"
                target="_blank"
                rel="noopener noreferrer"
                className="cy-btn cy-btn--primary"
                style={{
                  marginTop: 8,
                  justifyContent: "center",
                  padding: "12px 18px",
                  fontSize: 14,
                  textAlign: "center",
                }}
              >
                View on GitHub
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
